import { cacheHeaderToken, getCSRFToken } from './csrf'

/**
 * Mutating verbs whose request bodies we clone so we can replay them once
 * after a CSRF failure (see `attachInterceptors` below).
 */
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

/**
 * Endpoint templates where a 401 should NOT redirect to `/login`. These are
 * either family-portal flows where `ClientLayout` handles verification, or
 * Healthie iframe endpoints that may legitimately 401 mid-session.
 *
 * Compared against the templated `opts.url` (e.g. `/v1/clients/{shareable_id}/...`),
 * not the resolved request URL.
 */
const SKIP_LOGIN_REDIRECT: ReadonlySet<string> = new Set([
  '/v1/users/verify',
  '/v1/users/current_client',
  // FWE v2 per-student verification gate: a 401 here means "not verified for
  // this student's form yet", which OnboardingVerifyGate handles by showing the
  // identity screen — it must NOT bounce the family to /login. RCM-2114.
  '/v1/onboarding/{client_signed_id}/verification',
  '/v1/appointment_proxy',
  '/v1/clients/{shareable_id}/forthcoming_appointment',
  '/v1/clients/{shareable_id}/forthcoming_appointment/join',
  '/v1/scheduling/journeys/{journey_id}/context',
  '/v1/scheduling/journeys/{journey_id}/preferences/identity',
  '/v1/scheduling/journeys/{journey_id}/preferences/meeting-preference',
  '/v1/scheduling/journeys/{journey_id}/clinician_matches',
  '/v1/scheduling/journeys/{journey_id}/clinician_matches/{id}',
  '/v1/scheduling/journeys/{journey_id}/bookings',
])

const CSRF_ERROR_DETAIL = 'Invalid or expired CSRF token'

/**
 * Endpoint we hit to refresh the CSRF token. Any authenticated API endpoint
 * works — Rails' `Api::V1::BaseController` `after_action` writes a fresh
 * cookie + `X-CSRF-Token` response header on every successful response.
 */
const CSRF_REFRESH_URL = '/api/v1/users/current_user'

/**
 * Clones of mutating requests captured before fetch consumes the body, so we
 * can replay them with a fresh token after a CSRF 422. Keyed by the Request
 * object Hey API passes through both interceptors within a single call.
 */
const requestClones = new WeakMap<Request, Request>()

interface RequestOpts {
  /** Templated path (e.g. `/v1/clients/{shareable_id}/...`), not the resolved URL. */
  url: string
}

interface InterceptableClient {
  interceptors: {
    request: {
      use(fn: (request: Request, opts: RequestOpts) => Request | Promise<Request>): void
    }
    response: {
      use(
        fn: (
          response: Response,
          request: Request,
          opts: RequestOpts
        ) => Response | Promise<Response>
      ): void
    }
  }
}

function isCsrfError(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false
  const errors = (body as { errors?: unknown }).errors
  if (!Array.isArray(errors)) return false
  return errors.some(
    (e) => e && typeof e === 'object' && (e as { detail?: string }).detail === CSRF_ERROR_DETAIL
  )
}

/**
 * Attach Cartwheel's CSRF + 401-redirect behavior to a Hey API client.
 *
 * Each generated version (`generated/v1`, `generated/v2`, …) ships its own
 * client singleton, so the per-version entrypoint (`v1.ts`, `v2.ts`) calls
 * this once at import time.
 *
 * Behavior:
 *   - Every request carries the current CSRF token (cookie → meta tag →
 *     header-cache fallback).
 *   - Every response with an `X-CSRF-Token` header refreshes the cache, so
 *     same-origin JS keeps a usable token even when third-party cookies are
 *     blocked (Healthie iframe).
 *   - On a 422 carrying the `Invalid or expired CSRF token` error and a
 *     replayable mutating request, refresh the token and retry the original
 *     request once; the replay's response replaces the original 422.
 *   - On a 401 outside the skip list, redirect the page to `/login`.
 */
export function attachInterceptors(client: InterceptableClient): void {
  client.interceptors.request.use((request) => {
    if (MUTATING_METHODS.has(request.method)) {
      requestClones.set(request, request.clone())
    }
    request.headers.set('X-CSRF-Token', getCSRFToken())
    return request
  })

  client.interceptors.response.use(async (response, request, opts) => {
    const headerToken = response.headers.get('X-CSRF-Token')
    if (headerToken) cacheHeaderToken(headerToken)

    if (response.status === 422) {
      const clone = requestClones.get(request)
      if (clone) {
        const body = await response
          .clone()
          .json()
          .catch(() => null)
        if (isCsrfError(body)) {
          requestClones.delete(request)
          const refresh = await fetch(CSRF_REFRESH_URL, { credentials: 'include' })
          const refreshed = refresh.headers.get('X-CSRF-Token')
          if (refreshed) cacheHeaderToken(refreshed)
          clone.headers.set('X-CSRF-Token', getCSRFToken())
          return fetch(clone)
        }
      }
    }

    requestClones.delete(request)

    if (response.status === 401 && !SKIP_LOGIN_REDIRECT.has(opts.url)) {
      window.location.href = '/login'
    }

    return response
  })
}
